/**
 * CONFIG_OVER_CODE — client: operational configuration served by platform.
 *
 * `GET /api/config` returns ALREADY RESOLVED config (defaults → env →
 * `app_config` table → clamps). Nothing from that cascade is reimplemented here:
 * requested, normalized against code defaults (`mergePublicAppConfig`), and
 * cached in memory.
 *
 * ## Why there is a SYNCHRONOUS snapshot
 *
 * Cashsync is local-first: recurring engine and budget alerts
 * run at boot, **before** (and sometimes without) any network
 * call having responded, and must be able to run offline forever.
 * Thus:
 *
 *   * [`appConfig()`] ALWAYS returns something usable — initially, code
 *     defaults, which are historical literals;
 *   * [`refreshAppConfig()`] updates that snapshot when network responds;
 *   * a network failure **is not an error**: continues with previous snapshot.
 *
 * It is the difference between "platform can tune behavior" and "without
 * server the app cannot boot". Only the former is acceptable here.
 */
import {
  DEFAULT_PUBLIC_APP_CONFIG,
  mergePublicAppConfig,
  type PublicAppConfig,
} from "@cashsync/shared";

import { SERVER_URL } from "@/lib/runtime-config";

let snapshot: PublicAppConfig = DEFAULT_PUBLIC_APP_CONFIG;
let inFlight: Promise<PublicAppConfig> | null = null;

/** Active config. Never throws and never returns `undefined`. */
export function appConfig(): PublicAppConfig {
  return snapshot;
}

/**
 * Refreshes snapshot from server. Returns resulting config —
 * new one if call succeeded, previous one if not. **Does not propagate errors**: a
 * network failure cannot prevent the app from recording an expense.
 */
export async function refreshAppConfig(): Promise<PublicAppConfig> {
  inFlight ??= (async () => {
    try {
      const res = await fetch(`${SERVER_URL}/api/config`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) {
        return snapshot;
      }
      snapshot = mergePublicAppConfig(await res.json());
      return snapshot;
    } catch {
      // Offline, backend down, or unreadable response: continue
      // with what we already had. Exactly what local-first does.
      return snapshot;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}
